import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, FileText, TrendingUp, Clock, ChevronRight, LogOut, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import CreateContractModal from '@/components/CreateContractModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GenerIA Brand Logo URL
const GENERIA_LOGO = "https://customer-assets.emergentagent.com/job_inspeccion-pwa/artifacts/qhvfjidw_IMG_8814.jpeg";

export const ContratosPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { projects, setSelectedProject, setSelectedConcept, refreshProjects } = useApp();
  const [projectStats, setProjectStats] = useState({});
  const [showCreateContract, setShowCreateContract] = useState(false);

  // Load real stats for all projects
  useEffect(() => {
    const loadProjectStats = async () => {
      const stats = {};
      for (const project of projects) {
        try {
          // Get concept count
          const countRes = await axios.get(`${API}/concepts/count/${project.id}`);
          
          // Get all figures for progress calculation
          const figuresRes = await axios.get(`${API}/figures`, {
            params: { project_id: project.id }
          });
          const figures = figuresRes.data || [];
          
          // Get concepts for total contracted
          const conceptsRes = await axios.get(`${API}/concepts`, {
            params: { project_id: project.id }
          });
          const concepts = conceptsRes.data || [];
          
          // Calculate totals per concept
          const conceptTotals = {};
          figures.forEach(fig => {
            if (!conceptTotals[fig.concept_id]) {
              conceptTotals[fig.concept_id] = 0;
            }
            if (fig.tipo === 'suma') {
              conceptTotals[fig.concept_id] += fig.cantidad || 0;
            } else {
              conceptTotals[fig.concept_id] -= fig.cantidad || 0;
            }
          });
          
          // Calculate overall progress
          let totalContratado = 0;
          let totalAcumulado = 0;
          concepts.forEach(c => {
            totalContratado += c.contratado || 0;
            totalAcumulado += conceptTotals[c.id] || 0;
          });
          
          const avance = totalContratado > 0 ? (totalAcumulado / totalContratado) * 100 : 0;
          
          // Find last capture date
          let lastCapture = null;
          figures.forEach(fig => {
            const figDate = new Date(fig.created_at);
            if (!lastCapture || figDate > lastCapture) {
              lastCapture = figDate;
            }
          });
          
          stats[project.id] = {
            conceptos: countRes.data.count,
            avance: avance,
            ultimaCaptura: lastCapture 
              ? lastCapture.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
              : '-'
          };
        } catch (error) {
          stats[project.id] = { conceptos: 0, avance: 0, ultimaCaptura: '-' };
        }
      }
      setProjectStats(stats);
    };
    
    if (projects.length > 0) {
      loadProjectStats();
    }
  }, [projects]);

  const handleSelectContrato = (project) => {
    setSelectedProject(project);
    setSelectedConcept(null);
    navigate('/contrato'); // Navigate to detail page instead of catalogo
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const getProgressColor = (avance) => {
    if (avance > 100) return 'bg-[#FF6B00]';
    if (avance >= 75) return 'bg-green-500';
    if (avance >= 50) return 'bg-[#0066FF]';
    if (avance >= 25) return 'bg-[#00CFFF]';
    return 'bg-[#1E3550]';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550]">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <img src={GENERIA_LOGO} alt="GenerIA" className="h-12 w-12 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-[#E2EAF4] tracking-wide">GenerIA</h1>
                <p className="text-[#00CFFF] text-sm mono tagline">De la foto al generador.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-semibold text-[#E2EAF4]">{user?.name}</p>
                <p className="text-xs text-[#8BA3C7] capitalize">{user?.role === 'inspector' ? 'Residente' : user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-[#E2EAF4] hover:bg-[#1E3550]"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#E2EAF4] flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#0066FF]" />
              MIS CONTRATOS
            </h2>
            <p className="text-[#8BA3C7] text-sm mt-1">
              Selecciona un contrato para ver el catálogo de conceptos
            </p>
          </div>
          
          {/* Admin Controls */}
          {isAdmin && (
            <div className="flex gap-2">
              <Button
                onClick={() => setShowCreateContract(true)}
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                data-testid="btn-create-contract"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Contrato
              </Button>
            </div>
          )}
        </div>

        {/* Contract Cards */}
        <div className="space-y-4" data-testid="contratos-list">
          {projects.map((project, index) => {
            const stats = projectStats[project.id] || { conceptos: 0, avance: 0, ultimaCaptura: '-' };
            const barWidth = Math.min(100, stats.avance);
            const accentColor = index % 2 === 0 ? '#0066FF' : '#FF6B00';
            
            return (
              <div
                key={project.id}
                onClick={() => handleSelectContrato(project)}
                className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-5 cursor-pointer hover:border-[#0066FF]/50 hover:bg-[#0D1520]/80 transition-all"
                data-testid={`contrato-card-${project.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-1 h-8 rounded-full" 
                        style={{ backgroundColor: accentColor }}
                      />
                      <h3 className="text-lg font-bold text-[#E2EAF4]">
                        {project.name}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      {/* Conceptos */}
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#8BA3C7]" />
                        <div>
                          <p className="text-xs text-[#8BA3C7] uppercase">Conceptos</p>
                          <p className="font-semibold text-[#E2EAF4]">{stats.conceptos}</p>
                        </div>
                      </div>
                      
                      {/* Avance */}
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#8BA3C7]" />
                        <div>
                          <p className="text-xs text-[#8BA3C7] uppercase">Avance</p>
                          <p className={`font-semibold ${stats.avance > 100 ? 'text-[#FF6B00]' : 'text-[#00CFFF]'}`}>
                            {stats.avance.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      
                      {/* Última Captura */}
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#8BA3C7]" />
                        <div>
                          <p className="text-xs text-[#8BA3C7] uppercase">Última Captura</p>
                          <p className="font-semibold text-[#E2EAF4] text-sm">{stats.ultimaCaptura}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="h-2 bg-[#1E3550] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${getProgressColor(stats.avance)}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-6 h-6 text-[#8BA3C7] ml-4" />
                </div>
              </div>
            );
          })}

          {projects.length === 0 && (
            <div className="text-center py-12 bg-[#0D1520] rounded-xl border border-[#1E3550]">
              <Building2 className="w-16 h-16 text-[#1E3550] mx-auto mb-4" />
              <p className="text-[#8BA3C7]">No tienes contratos asignados</p>
            </div>
          )}
        </div>
      </main>

      {/* Create Contract Modal - Admin Only */}
      {showCreateContract && (
        <CreateContractModal
          onClose={() => setShowCreateContract(false)}
          onSuccess={() => {
            refreshProjects?.();
            setShowCreateContract(false);
          }}
        />
      )}
    </div>
  );
};

export default ContratosPage;
